import { useState } from "react";
import type { FormEvent } from "react";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

function commandParts(command: string) {
  return command.trim().split(/\s+/).filter(Boolean);
}

export function TerminalApp({ activeMachine, app, onRunAlias, onRunCommand, profile }: SimulatorAppSurfaceProps) {
  const [command, setCommand] = useState("");
  const [lines, setLines] = useState<string[]>([
    `FirstDesk terminal on ${activeMachine}`,
    `${profile.productLabel} ready.`
  ]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const raw = command.trim();
    if (!raw) return;
    setCommand("");
    setLines((current) => [...current, `> ${raw}`]);

    const parts = commandParts(raw);
    const lower = parts.map((part) => part.toLowerCase());

    if (onRunAlias(raw, "terminal")) {
      setLines((current) => [...current, `Opened ${raw}.`]);
      return;
    }

    if (lower[0] === "net" && lower[1] === "user" && parts[2] && lower.includes("/add")) {
      const username = parts[2];
      const result = await onRunCommand({
        appId: "lusrmgr",
        commandType: "LOCAL_USER_CREATE",
        capability: "local.user.write",
        label: `Create ${username}`,
        payload: { fullName: username, machineId: activeMachine, username }
      });
      setLines((current) => [...current, result.ok ? `User ${username} created.` : `Command rejected (${result.status}).`]);
      return;
    }

    if (lower.includes("install-windowsfeature") || lower.includes("install-feature")) {
      const role = parts.find((part) => !part.startsWith("-") && !part.toLowerCase().includes("install")) ?? "AD-Domain-Services";
      const result = await onRunCommand({
        appId: "server-manager",
        commandType: "SERVER_ROLE_INSTALL",
        capability: "serverRoles.write",
        label: `Install ${role}`,
        payload: { machineId: activeMachine, role, roleName: role }
      });
      setLines((current) => [...current, result.ok ? `Role ${role} installed.` : `Role install rejected (${result.status}).`]);
      return;
    }

    if (lower[0] === "hostname") {
      setLines((current) => [...current, activeMachine]);
      return;
    }

    const result = await onRunCommand({
      appId: app.id,
      commandType: "RECORD_APP_CHECK",
      capability: app.launchCapability ?? "terminal.command.write",
      label: raw,
      payload: { appId: app.id, command: raw, machineId: activeMachine, title: app.title }
    });
    setLines((current) => [...current, result.ok ? "Command recorded." : `Command rejected (${result.status}).`]);
  }

  return (
    <div className="fd-sim-app fd-sim-terminal">
      <div className="fd-terminal-output" aria-live="polite">
        {lines.slice(-12).map((line, index) => (
          <div key={`${line}-${index}`}>{line}</div>
        ))}
      </div>
      <form className="fd-terminal-input" onSubmit={(event) => void submit(event)}>
        <span>{activeMachine}&gt;</span>
        <input
          autoFocus
          aria-label="Terminal command"
          spellCheck={false}
          value={command}
          onChange={(event) => setCommand(event.target.value)}
        />
      </form>
    </div>
  );
}
